/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * Layout Store
 *
 * Persists user's preferred module navigation layout style to localStorage.
 * Also stores whether to use module logos (SVGs) or Lucide icons
 * in the rail/topbar switcher buttons.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { LayoutStyle } from '@/config/module-registry';
import type { BottomNavStyle, MobileModuleSwitchStyle } from '@/components/layouts/mobile-bottom-nav/types';

export type DocTypeSelectorStyle = 'tabs' | 'dropdown' | 'grid';
export type FavoritesDisplayStyle = 'icons' | 'chips' | 'palette';

export const FAVORITES_MAX: Record<FavoritesDisplayStyle, number> = {
  icons: 8,
  chips: 5,
  palette: 10,
};

interface LayoutState {
  layoutStyle: LayoutStyle;
  /** Desktop-only: collapse the module sidebar to an icons-only rail */
  sidebarCollapsed: boolean;
  useModuleLogos: boolean;
  bottomNavStyle: BottomNavStyle;
  mobileModuleSwitchStyle: MobileModuleSwitchStyle;
  docTypeSelectorStyle: DocTypeSelectorStyle;
  favorites: string[];
  favoritesDisplayStyle: FavoritesDisplayStyle;
  setDocTypeSelectorStyle: (style: DocTypeSelectorStyle) => void;
  setLayoutStyle: (style: LayoutStyle) => void;
  toggleSidebarCollapsed: () => void;
  setUseModuleLogos: (value: boolean) => void;
  setBottomNavStyle: (style: BottomNavStyle) => void;
  setMobileModuleSwitchStyle: (style: MobileModuleSwitchStyle) => void;
  addFavorite: (href: string) => void;
  removeFavorite: (href: string) => void;
  reorderFavorites: (fromIndex: number, toIndex: number) => void;
  setFavoritesDisplayStyle: (style: FavoritesDisplayStyle) => void;
}

export const useLayoutStore = create<LayoutState>()(
  persist(
    (set) => ({
      layoutStyle: 'dropdown',
      sidebarCollapsed: false,
      useModuleLogos: false,
      bottomNavStyle: 'grid' as BottomNavStyle,
      mobileModuleSwitchStyle: 'tab' as MobileModuleSwitchStyle,
      docTypeSelectorStyle: 'tabs' as DocTypeSelectorStyle,
      favorites: [],
      favoritesDisplayStyle: 'palette' as FavoritesDisplayStyle,
      setDocTypeSelectorStyle: (style) => set({ docTypeSelectorStyle: style }),
      setLayoutStyle: (style) => set({ layoutStyle: style }),
      toggleSidebarCollapsed: () => set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setUseModuleLogos: (value) => set({ useModuleLogos: value }),
      setBottomNavStyle: (style) => set({ bottomNavStyle: style }),
      setMobileModuleSwitchStyle: (style) => set({ mobileModuleSwitchStyle: style }),
      addFavorite: (href) => set((s) => {
        if (s.favorites.includes(href)) return s;
        const max = FAVORITES_MAX[s.favoritesDisplayStyle];
        if (s.favorites.length >= max) return s;
        return { favorites: [...s.favorites, href] };
      }),
      removeFavorite: (href) => set((s) => ({
        favorites: s.favorites.filter((h) => h !== href),
      })),
      reorderFavorites: (from, to) => set((s) => {
        if (from === to) return s;
        const len = s.favorites.length;
        if (from < 0 || from >= len || to < 0 || to >= len) return s;
        const next = [...s.favorites];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return { favorites: next };
      }),
      setFavoritesDisplayStyle: (style) => set((s) => {
        const max = FAVORITES_MAX[style];
        const favorites = s.favorites.length > max ? s.favorites.slice(0, max) : s.favorites;
        return { favoritesDisplayStyle: style, favorites };
      }),
    }),
    {
      name: 'corex-layout-style',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export const useLayoutStyle = () => useLayoutStore((s) => s.layoutStyle);
export const useSidebarCollapsed = () => useLayoutStore((s) => s.sidebarCollapsed);
export const useModuleLogos = () => useLayoutStore((s) => s.useModuleLogos);
export const useBottomNavStyle = () => useLayoutStore((s) => s.bottomNavStyle);
export const useMobileModuleSwitchStyle = () => useLayoutStore((s) => s.mobileModuleSwitchStyle);
export const useFavorites = () => useLayoutStore((s) => s.favorites);
export const useFavoritesDisplayStyle = () => useLayoutStore((s) => s.favoritesDisplayStyle);
export const useDocTypeSelectorStyle = () => useLayoutStore((s) => s.docTypeSelectorStyle);
